/**
 * Constantes pour les endpoints API
 * Ce fichier centralise tous les endpoints de l'API pour éviter la duplication
 * et faciliter la maintenance
 */

import { createEndpoint } from "./api.helpers"; // Import depuis le fichier .ts

// Interfaces pour typer les fonctions génératrices d'endpoints
interface EndpointFunction {
  (id: string | number): string;
}

interface OrgSlugEndpointFunction {
  (orgSlug: string): string;
}

interface OrgSlugAndIdEndpointFunction {
  (orgSlug: string, id: string | number): string;
}

interface OrgSlugIdAndSpotIdEndpointFunction {
  (orgSlug: string, lakeId: string | number, spotId: string | number): string;
}

// Structure principale des endpoints API
export const AUTH = {
  LOGIN: createEndpoint("auth/jwt/login"),
  REGISTER: createEndpoint("auth/register"),
  LOGOUT: createEndpoint("auth/jwt/logout"),
  REQUEST_DEMO_ACCESS: createEndpoint("auth/request-demo-access"),
  FORGOT_PASSWORD: createEndpoint("auth/forgot-password"),
  RESET_PASSWORD: createEndpoint("auth/reset-password"),
};

export const USERS = {
  ME: createEndpoint("users/me"),
  ME_AVATAR: createEndpoint("users/me/avatar"),
  ME_PASSWORD: createEndpoint("users/me/password"),
  ME_NOTIFICATION_PREFERENCES: createEndpoint("users/me/notification-preferences"),
  LIST: createEndpoint("users"),
  DETAILS: ((id: string | number) =>
    createEndpoint(`users/${id}`)) as EndpointFunction,
  UPDATE: ((id: string | number) =>
    createEndpoint(`users/${id}`)) as EndpointFunction,
  DELETE: ((id: string | number) =>
    createEndpoint(`users/${id}`)) as EndpointFunction,
};

export const ORGANIZATION_USERS = {
  // Trailing slash intentional: matches the backend route exactly (GET "/")
  // so the request never hits Starlette's redirect_slashes — that redirect
  // builds an absolute http:// Location behind the TLS-terminating proxy,
  // which the browser then blocks as mixed content.
  LIST: createEndpoint("organization-users/"),
  INVITE: createEndpoint("organization-users/invite"),
  UPDATE_PERMISSIONS: ((id: string | number) =>
    createEndpoint(`organization-users/${id}/permissions`)) as EndpointFunction,
  REVOKE: ((id: string | number) =>
    createEndpoint(`organization-users/${id}`)) as EndpointFunction,
  DETAILS: ((id: string | number) =>
    createEndpoint(`organization-users/${id}`)) as EndpointFunction,
  LAKE_PERMISSIONS: ((id: string | number) =>
    createEndpoint(`organization-users/${id}/lake-permissions`)) as EndpointFunction,
};

export const ORGANIZATIONS = {
  LIST: createEndpoint("organizations"),
  CREATE: createEndpoint("organizations"),
  ME: createEndpoint("organizations/me"),
  UPDATE: createEndpoint("organizations/me"),
  LEGAL: createEndpoint("organizations/me/legal"),
  EMAIL_BRANDING: createEndpoint("organizations/me/email-branding"),
  GOOGLE_REVIEWS_SETTINGS: createEndpoint("organizations/me/google-reviews/settings"),
  GOOGLE_REVIEWS_STATISTICS: createEndpoint("organizations/me/google-reviews/statistics"),
  GOOGLE_REVIEWS_PROCESS: createEndpoint("organizations/me/google-reviews/process"),
  DETAILS: ((slug: string) => createEndpoint(`organizations/${slug}`)) as (
    slug: string,
  ) => string,
  DELETE: ((slug: string) => createEndpoint(`organizations/${slug}`)) as (
    slug: string,
  ) => string,
};

export const LAKES = {
  LIST: createEndpoint("lakes"),
  DETAILS: ((id: string | number) =>
    createEndpoint(`lakes/${id}`)) as EndpointFunction,
  CREATE: createEndpoint("lakes"),
  UPDATE: ((id: string | number) =>
    createEndpoint(`lakes/${id}`)) as EndpointFunction,
  DELETE: ((id: string | number) =>
    createEndpoint(`lakes/${id}`)) as EndpointFunction,
  ACTIVATE: ((id: string | number) =>
    createEndpoint(`lakes/${id}/activate`)) as EndpointFunction,
  DEACTIVATE: ((id: string | number) =>
    createEndpoint(`lakes/${id}/deactivate`)) as EndpointFunction,
  // Image management endpoints
  DELETE_IMAGE: ((id: string | number, fieldName: string) =>
    createEndpoint(`lakes/${id}/image/${fieldName}`)) as (
    id: string | number,
    fieldName: string,
  ) => string,
  ADD_GALLERY_IMAGES: ((id: string | number) =>
    createEndpoint(`lakes/${id}/gallery`)) as EndpointFunction,
  DELETE_GALLERY_IMAGE: ((id: string | number) =>
    createEndpoint(`lakes/${id}/gallery/delete`)) as EndpointFunction,
  UPDATE_GALLERY_ORDER: ((id: string | number) =>
    createEndpoint(`lakes/${id}/gallery/order`)) as EndpointFunction,
};

/**
 * Réglages de communication d'un lac (branding email, SMS, avis Google).
 * Les réglages de l'organisation restent sous ORGANIZATIONS (SMS.* pour le
 * volet SMS) : un lac n'hérite que de ce qu'il ne personnalise pas.
 */
export const LAKE_COMMUNICATIONS = {
  SETTINGS: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/communication-settings`)) as EndpointFunction,
};

/**
 * Jeton de synchronisation iCal d'un lac. Scopé par lac (pas org-wide) :
 * voir LakeIcalToken côté backend pour le pourquoi.
 */
export const LAKE_ICAL = {
  TOKEN: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/ical-token`)) as EndpointFunction,
  REGENERATE: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/ical-token/regenerate`)) as EndpointFunction,
};

export const FISHING_SPOTS = {
  LIST: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots`)) as EndpointFunction,
  LIST_ALL: createEndpoint('fishing-spots'),
  DETAILS: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
  CREATE: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots`)) as EndpointFunction,
  UPDATE: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
  DELETE: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
  UPDATE_STATUS: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
  UPLOAD_IMAGE: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}/image`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
  DELETE_IMAGE: ((lakeId: string | number, spotId: string | number) =>
    createEndpoint(`lakes/${lakeId}/spots/${spotId}/image`)) as (
    lakeId: string | number,
    spotId: string | number,
  ) => string,
};

export const RESERVATIONS = {
  LIST: createEndpoint("reservations"),
  CREATE: createEndpoint("reservations"),
  DETAILS: ((id: string | number) =>
    createEndpoint(`reservations/${id}`)) as EndpointFunction,
  UPDATE: ((id: string | number) =>
    createEndpoint(`reservations/${id}`)) as EndpointFunction,
  DELETE: ((id: string | number) =>
    createEndpoint(`reservations/${id}`)) as EndpointFunction,
  CHECK_AVAILABILITY: createEndpoint("reservations/check-availability"),
  CREATE_PAYMENT: ((id: string | number) =>
    createEndpoint(`reservations/${id}/payment`)) as EndpointFunction,
};

export const CHECKIN = {
  SCAN: createEndpoint("checkin/scan"),
  PRESENCES: createEndpoint("checkin/presences"),
  EXPECTED: createEndpoint("checkin/expected"),
  HISTORY: ((id: string | number) =>
    createEndpoint(`checkin/history/${id}`)) as EndpointFunction,
  MANUAL: ((id: string | number) =>
    createEndpoint(`checkin/manual/${id}`)) as EndpointFunction,
  CHECKOUT: ((id: string | number) =>
    createEndpoint(`checkin/${id}/checkout`)) as EndpointFunction,
  SYNC: createEndpoint("checkin/sync"),
  QR: ((id: string | number) => createEndpoint(`checkin/qr/${id}`)) as EndpointFunction,
};

export const RESERVATION_OPTION_CONFIGS = {
  LIST: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/pricing/option-configs`)) as EndpointFunction,
  DETAILS: ((lakeId: string | number, id: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/option-configs/${id}`)) as (
    lakeId: string | number,
    id: string,
  ) => string,
  CREATE: ((lakeId: string | number) =>
    createEndpoint(`lakes/${lakeId}/pricing/option-configs`)) as EndpointFunction,
  UPDATE: ((lakeId: string | number, id: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/option-configs/${id}`)) as (
    lakeId: string | number,
    id: string,
  ) => string,
  DELETE: ((lakeId: string | number, id: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/option-configs/${id}`)) as (
    lakeId: string | number,
    id: string,
  ) => string,
};

export const MODULES = {
  STATE: createEndpoint("organization-modules"),
  TOGGLE: ((name: string) =>
    createEndpoint(`organization-modules/${name}`)) as EndpointFunction,
};

export const SMS = {
  SETTINGS: createEndpoint("communications/sms-settings"),
  TEMPLATE: ((templateType: string) =>
    createEndpoint(`communications/sms-templates/${templateType}`)) as EndpointFunction,
  TEMPLATE_RESET: ((templateType: string) =>
    createEndpoint(`communications/sms-templates/${templateType}/reset`)) as EndpointFunction,
  CREDIT_REFRESH: createEndpoint("sms/credit/refresh"),
};
export const SMS_TEST = createEndpoint("sms/test");

export const PUBLIC = {
  LAKES: {
    LIST: ((orgSlug: string) =>
      createEndpoint(`public/${orgSlug}/lakes`)) as OrgSlugEndpointFunction,
    DETAILS: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}`,
      )) as OrgSlugAndIdEndpointFunction,
    CANCELLATION_POLICY: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/cancellation-policy`,
      )) as OrgSlugAndIdEndpointFunction,
    FISHING_SPOTS: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/fishing-spots`,
      )) as OrgSlugAndIdEndpointFunction,
    RESERVATION_OPTIONS: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/reservation-options`,
      )) as OrgSlugAndIdEndpointFunction,
    LODGINGS: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/lodgings`,
      )) as OrgSlugAndIdEndpointFunction,
  },
  MARKETPLACE: {
    SEARCH: createEndpoint("public/lakes/search"),
    FACETS: createEndpoint("public/lakes/facets"),
  },
  BOOSTED: createEndpoint("public/lakes/boosted"),
  AVAILABILITY: {
    // NB: lakeId kept in the signature for call-site compatibility, but the
    // backend route (public_spot_availability.py) is scoped by spot only.
    CHECK: ((
      orgSlug: string,
      _lakeId: string | number,
      spotId: string | number,
    ) =>
      createEndpoint(
        `public/${orgSlug}/spots/${spotId}/availability`,
      )) as OrgSlugIdAndSpotIdEndpointFunction,
    AGGREGATED: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/availability/aggregated`,
      )) as OrgSlugAndIdEndpointFunction,
    OVERVIEW: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/availability`,
      )) as OrgSlugAndIdEndpointFunction,
    CALENDAR: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/availability/calendar`,
      )) as OrgSlugAndIdEndpointFunction,
  },
  CONFIG: {
    ORGANIZATION: ((orgSlug: string) =>
      createEndpoint(`public/${orgSlug}/config`)) as OrgSlugEndpointFunction,
    TIANJI: createEndpoint("public/config/tianji"),
  },
  RESERVATIONS: {
    CREATE: ((orgSlug: string, lakeId: string | number) =>
      createEndpoint(
        `public/${orgSlug}/lakes/${lakeId}/reservations`,
      )) as OrgSlugAndIdEndpointFunction,
    RESOLVE_SESSION: ((orgSlug: string) =>
      createEndpoint(
        `public/${orgSlug}/reservations/resolve-session`,
      )) as OrgSlugEndpointFunction,
  },
  CHECKIN: {
    QR: ((orgSlug: string, reservationId: string | number) =>
      createEndpoint(`public/${orgSlug}/reservation/${reservationId}/qr`)) as OrgSlugAndIdEndpointFunction,
  },
  LEGAL: {
    CGV: ((orgSlug: string) =>
      createEndpoint(`public/${orgSlug}/cgv`)) as OrgSlugEndpointFunction,
    MENTIONS: ((orgSlug: string) =>
      createEndpoint(
        `public/${orgSlug}/mentions-legales`,
      )) as OrgSlugEndpointFunction,
  },
};

export const MANAGER_DASHBOARD = {
  STATS: createEndpoint("manager-dashboard/stats"),
  TIMELINE: createEndpoint("manager-dashboard/timeline"),
  DAILY_SUMMARY: createEndpoint("manager-dashboard/daily-summary"),
  REVENUE_BREAKDOWN: createEndpoint("manager-dashboard/revenue-breakdown"),
  CUSTOMERS: createEndpoint("manager-dashboard/customers"),
};

export const MANAGER_BOOSTS = {
  PLANS: ((lakeId: string) =>
    createEndpoint(`manager/lakes/${lakeId}/boost/plans`)) as (
    lakeId: string,
  ) => string,
  CHECKOUT: ((lakeId: string) =>
    createEndpoint(`manager/lakes/${lakeId}/boost/checkout`)) as (
    lakeId: string,
  ) => string,
  SUMMARY: ((lakeId: string) =>
    createEndpoint(`manager/lakes/${lakeId}/boost`)) as (
    lakeId: string,
  ) => string,
  LIST: createEndpoint("manager/boosts"),
  STATS: ((boostId: string) =>
    createEndpoint(`manager/boosts/${boostId}/stats`)) as EndpointFunction,
};

export const ADMIN_BOOSTS = {
  LIST: createEndpoint("admin/boosts"),
  CANCEL: ((boostId: string) =>
    createEndpoint(`admin/boosts/${boostId}/cancel`)) as EndpointFunction,
  REFUND: ((boostId: string) =>
    createEndpoint(`admin/boosts/${boostId}/refund`)) as EndpointFunction,
};

export const ADMIN_DASHBOARD = {
  STATS: createEndpoint("dashboard/stats"),
  TIMELINE: createEndpoint("dashboard/timeline"),
};

export const SUBSCRIPTIONS = {
  PLANS: createEndpoint("billing/plans"),
  STATUS: createEndpoint("billing/subscription"),
  CHECKOUT_SESSION: createEndpoint("billing/subscription/checkout-session"),
  PORTAL_SESSION: createEndpoint("billing/subscription/portal-session"),
  INVOICES: createEndpoint("billing/invoices"),
  ENTITLEMENTS: createEndpoint("billing/entitlements"),
  /** Achat ponctuel d'une recharge de crédits SMS (hors abonnement). */
  SMS_CREDITS_CHECKOUT: createEndpoint("billing/sms-credits/checkout-session"),
  /** @deprecated Use PORTAL_SESSION */
  CUSTOMER_PORTAL: createEndpoint("billing/subscription/portal-session"),
};

export const ADMIN_USERS = {
  LIST: createEndpoint("admin/users"),
  DETAILS: ((userId: string) =>
    createEndpoint(`admin/users/${userId}`)) as EndpointFunction,
  DELETE: ((userId: string) =>
    createEndpoint(`admin/users/${userId}`)) as EndpointFunction,
  UPDATE_STATUS: ((userId: string) =>
    createEndpoint(`admin/users/${userId}/status`)) as EndpointFunction,
};

export const NOTIFICATIONS = {
  LIST: createEndpoint("notifications"),
};

export const CONTACT = {
  SUBMIT: createEndpoint("contact"),
};

export const STRIPE_CONNECT = {
  ONBOARDING_LINK: createEndpoint("stripe-connect/onboarding-link"),
  ACCOUNT_STATUS: createEndpoint("stripe-connect/account-status"),
};

export const PRICING_INTEGRATION = {
  INITIALIZE_LAKE: ((lakeId: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/initialize`)) as EndpointFunction,
  LAKE_STATUS: ((lakeId: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/status`)) as EndpointFunction,
  LAKE_ANALYSIS: ((lakeId: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/analysis`)) as EndpointFunction,
  SYNC_SPOT: ((lakeId: string, spotId: string) =>
    createEndpoint(`lakes/${lakeId}/pricing/spots/${spotId}/sync`)) as (
    lakeId: string,
    spotId: string,
  ) => string,
};

export const CAMPAIGNS = {
  LIST: createEndpoint("campaigns"),
  CREATE: createEndpoint("campaigns"),
  DETAIL: ((id: string) => createEndpoint(`campaigns/${id}`)) as EndpointFunction,
  UPDATE: ((id: string) => createEndpoint(`campaigns/${id}`)) as EndpointFunction,
  DELETE: ((id: string) => createEndpoint(`campaigns/${id}`)) as EndpointFunction,
  GENERATE: ((id: string) => createEndpoint(`campaigns/${id}/generate`)) as EndpointFunction,
  REWRITE: ((id: string) => createEndpoint(`campaigns/${id}/rewrite`)) as EndpointFunction,
  UPDATE_ASSETS: ((id: string) =>
    createEndpoint(`campaigns/${id}/assets`)) as EndpointFunction,
  AUDIENCE_PREVIEW: ((id: string) =>
    createEndpoint(`campaigns/${id}/audience/preview`)) as EndpointFunction,
  VISUALS_GENERATE: ((id: string) =>
    createEndpoint(`campaigns/${id}/visuals/generate`)) as EndpointFunction,
  VISUALS_LIST: ((id: string) =>
    createEndpoint(`campaigns/${id}/visuals`)) as EndpointFunction,
  EXECUTE: ((id: string) => createEndpoint(`campaigns/${id}/execute`)) as EndpointFunction,
  PERFORMANCE: ((id: string) =>
    createEndpoint(`campaigns/${id}/performance`)) as EndpointFunction,
  PREVIEW_EMAIL: ((id: string) =>
    createEndpoint(`campaigns/${id}/preview-email`)) as EndpointFunction,
};

// Exportation par défaut de tous les groupes d'endpoints
const apiEndpoints = {
  AUTH,
  USERS,
  ORGANIZATION_USERS,
  ORGANIZATIONS,
  LAKES,
  LAKE_COMMUNICATIONS,
  LAKE_ICAL,
  FISHING_SPOTS,
  RESERVATIONS,
  CHECKIN,
  RESERVATION_OPTION_CONFIGS,
  PUBLIC,
  MODULES,
  MANAGER_DASHBOARD,
  MANAGER_BOOSTS,
  ADMIN_DASHBOARD,
  SUBSCRIPTIONS,
  ADMIN_USERS,
  NOTIFICATIONS,
  CONTACT,
  STRIPE_CONNECT,
  PRICING_INTEGRATION,
  ADMIN_BOOSTS,
  CAMPAIGNS,
};
export default apiEndpoints;
